/**
 * Admin tab components for: Régates, Codes promo, Agenda.
 * Used by AdminDashboard.jsx.
 */
import React, { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import {
  Plus, Trash2, Upload, X, FileText, Tag, CalendarDays, Trophy, Check, BellRing, Images,
  GripVertical, Eye, EyeOff, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { tr } from "../contexts/LangContext";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "blockquote"],
    ["clean"],
  ],
};

/* ============================================================== */
/* RÉGATES TAB                                                     */
/* ============================================================== */
const REG_DOC_TYPES = [
  { key: "notice_of_race",       label: tr("Avis de course")},
  { key: "sailing_instructions", label: tr("Instructions de course")},
  { key: "results",              label: "Classements" },
  { key: "general_ranking",      label: tr("Classement général")},
];

export function RegattasTab() {
  const [regattas, setRegattas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // slug being edited, or null
  const empty = {
    slug: "", name: "", date: "", end_date: "", location: "Île Monsin, Liège",
    description_fr: "", description_nl: "", description_en: "",
    image: "https://images.unsplash.com/photo-1498623116890-37e912163d5d?w=1600",
  };
  const [form, setForm] = useState(empty);
  const [docsRegatta, setDocsRegatta] = useState(null);
  const [err, setErr] = useState("");
  const backend = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = (u) => (u && u.startsWith("/uploads/") ? `${backend}/api${u}` : u);

  const load = async () => {
    try {
      const { data } = await api.get("/regattas");
      setRegattas(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const startEdit = (r) => {
    setEditing(r.slug);
    setForm({
      slug: r.slug, name: r.name, date: r.date, end_date: r.end_date || "",
      location: r.location || "", image: r.image || "",
      description_fr: r.description_fr || "", description_nl: r.description_nl || "", description_en: r.description_en || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      if (editing) {
        // Edit — send JSON PATCH (schema RegattaCreate)
        await api.patch(`/admin/regattas/${editing}`, form);
        toast.success(tr("Régate mise à jour"));
      } else {
        await api.post("/admin/regattas", form);
        toast.success(tr("Régate créée"));
      }
      setShowForm(false); setEditing(null); setForm(empty);
      load();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };

  const uploadImage = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/regattas/upload-image", fd);
      setForm((f) => ({ ...f, image: data.url }));
      toast.success(tr("Image uploadée"));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur upload"); }
  };

  const del = async (slug) => {
    if (!window.confirm(tr("Supprimer cette régate et tous ses documents ?"))) return;
    await api.delete(`/admin/regattas/${slug}`);
    load();
  };

  return (
    <div data-testid="admin-regattas">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-serif text-2xl text-navy">Régates ({regattas.length})</h3>
        <button onClick={() => showForm ? setShowForm(false) : startCreate()}
          className="inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red" data-testid="regatta-add-toggle">
          <Plus className="w-4 h-4" />{tr("Nouvelle régate")}</button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="regatta-form">
          <div className="md:col-span-2 flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-widest text-club-red font-semibold">
              {editing ? `Modification : ${editing}` : tr("Nouvelle régate")}
            </span>
          </div>
          <input required placeholder="Slug (ex: coupe-2027)" value={form.slug} readOnly={!!editing}
            onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
            className={`border border-slate-300 px-3 py-2 ${editing ? "bg-slate-100 text-slate-500" : ""}`} data-testid="regatta-slug" />
          <input required placeholder={tr("Nom")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="regatta-name" />
          <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="regatta-date" />
          <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="regatta-enddate" />
          <input placeholder={tr("Lieu")} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="regatta-location" />
          {/* Image URL + upload */}
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{tr("Image / photo de couverture")}</label>
            <div className="flex items-start gap-3">
              {form.image && <img src={fullUrl(form.image)} alt={tr("Aperçu")} className="w-40 h-24 object-cover bg-slate-100" />}
              <div className="flex-1">
                <input placeholder={tr("URL de l'image ou /uploads/regattas/…")} value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
                  className="w-full border border-slate-300 px-3 py-2 mb-2" data-testid="regatta-image" />
                <label className="inline-flex items-center gap-2 bg-club-yellow text-navy px-4 py-2 text-xs uppercase tracking-widest cursor-pointer hover:bg-club-yellow-dark" data-testid="regatta-image-upload-label">
                  <Upload className="w-3 h-3" /> Uploader une image
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])} data-testid="regatta-image-upload-input" />
                </label>
              </div>
            </div>
          </div>
          <textarea placeholder="Description FR" value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2 min-h-20" data-testid="regatta-desc-fr" />
          <textarea placeholder="Description NL" value={form.description_nl} onChange={(e) => setForm({ ...form, description_nl: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="regatta-desc-nl" />
          <textarea placeholder="Description EN" value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="regatta-desc-en" />
          {err && <div className="md:col-span-2 text-red-600 text-sm" data-testid="regatta-err">{err}</div>}
          <div className="md:col-span-2 flex gap-2 justify-end">
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
              className="px-5 py-3 text-xs uppercase tracking-[0.2em] text-slate-600" data-testid="regatta-cancel">{tr("Annuler")}</button>
            <button type="submit" className="bg-club-red text-white py-3 px-6 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="regatta-submit">
              {editing ? tr("Enregistrer") : tr("Créer")}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {regattas.map((r) => (
          <div key={r.id || r.slug} className="bg-white border border-slate-200 overflow-hidden" data-testid={`admin-regatta-${r.slug}`}>
            {r.image && (
              <div className="aspect-video overflow-hidden bg-slate-100">
                <img src={fullUrl(r.image)} alt={r.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-5">
              <Trophy className="w-6 h-6 text-club-red mb-3" strokeWidth={1.5} />
              <div className="font-serif text-xl text-navy">{r.name}</div>
              <div className="text-xs text-slate-500 mt-1">{r.date}</div>
              <div className="text-xs uppercase tracking-widest text-club-yellow-dark mt-1">{r.location}</div>
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => startEdit(r)}
                  className="text-[11px] uppercase tracking-widest text-club-yellow-dark border border-club-yellow-dark px-3 py-1.5 hover:bg-club-yellow"
                  data-testid={`regatta-edit-${r.slug}`}>{tr("Modifier")}</button>
                <button onClick={() => setDocsRegatta(r)} className="text-[11px] uppercase tracking-widest text-navy border border-navy px-3 py-1.5 hover:bg-navy hover:text-white" data-testid={`regatta-docs-btn-${r.slug}`}>
                  <Upload className="w-3 h-3 inline mr-1" /> Documents
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Envoyer le rappel 48 h pour la régate « ${r.name} » maintenant ?`)) return;
                    try {
                      const { data } = await api.post(`/admin/regattas/${r.slug}/send-reminder`);
                      toast.success(`${tr("Rappel envoyé —")} ${data.recipients} ${tr("destinataires")}`);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || tr("Erreur lors de l'envoi"));
                    }
                  }}
                  className="text-[11px] uppercase tracking-widest text-amber-700 border border-amber-700 px-3 py-1.5 hover:bg-amber-700 hover:text-white"
                  data-testid={`regatta-remind-${r.slug}`}
                  title={tr("Envoyer le rappel 48 h maintenant")}
                >
                  <BellRing className="w-3 h-3 inline mr-1" /> Rappel
                </button>
                <button onClick={() => del(r.slug)} className="text-[11px] uppercase tracking-widest text-red-600 hover:underline ml-auto" data-testid={`regatta-del-${r.slug}`}>{tr("Supprimer")}</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {docsRegatta && <RegattaDocsModal regatta={docsRegatta} onClose={() => setDocsRegatta(null)} />}
    </div>
  );
}

function RegattaDocsModal({ regatta, onClose }) {
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("notice_of_race");
  const [title, setTitle] = useState("");
  const [raceNumber, setRaceNumber] = useState(0);
  const [file, setFile] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/regattas/${regatta.slug}/documents`);
      setDocs(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [regatta.slug]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { setErr(tr("Sélectionnez un fichier PDF")); return; }
    setErr("");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    fd.append("title", title || file.name);
    fd.append("race_number", raceNumber || 0);
    try {
      await api.post(`/admin/regattas/${regatta.slug}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null); setTitle(""); setRaceNumber(0);
      load();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur upload"); }
  };

  const del = async (id) => {
    if (!window.confirm(tr("Supprimer ce document ?"))) return;
    await api.delete(`/admin/regattas/${regatta.slug}/documents/${id}`);
    load();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="regatta-docs-modal" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full p-8 relative my-12" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="regatta-docs-close">
          <X className="w-5 h-5" />
        </button>
        <div className="label-eyebrow text-club-red mb-2">Documents officiels</div>
        <h2 className="font-serif text-2xl text-navy">{regatta.name}</h2>
        <div className="divider-gold mt-4 mb-6" />

        <form onSubmit={upload} className="bg-sand border border-slate-200 p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="regatta-upload-form">
          <select value={docType} onChange={(e) => setDocType(e.target.value)} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="regatta-upload-type">
            {REG_DOC_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
          <input placeholder={tr("Titre (optionnel)")} value={title} onChange={(e) => setTitle(e.target.value)} className="border border-slate-300 px-3 py-2" data-testid="regatta-upload-title" />
          <input type="number" placeholder={tr("N° course (pour Classements)")} value={raceNumber} onChange={(e) => setRaceNumber(e.target.value)} className="border border-slate-300 px-3 py-2" data-testid="regatta-upload-race" />
          <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])} className="md:col-span-2 text-sm" data-testid="regatta-upload-file" />
          {err && <div className="md:col-span-2 text-red-600 text-sm">{err}</div>}
          <button type="submit" className="md:col-span-2 bg-club-red text-white py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="regatta-upload-submit">
            <Upload className="w-3.5 h-3.5 inline mr-2" /> Téléverser PDF
          </button>
        </form>

        {REG_DOC_TYPES.map(({ key, label }) => {
          const items = docs.filter((d) => d.doc_type === key);
          return (
            <div key={key} className="mb-5" data-testid={`regatta-docs-section-${key}`}>
              <h4 className="font-serif text-lg text-navy mb-2">{label} <span className="text-xs text-slate-500">({items.length})</span></h4>
              {items.length === 0 ? (
                <div className="text-xs text-slate-400 border border-dashed border-slate-300 p-3">—</div>
              ) : items.map((d) => (
                <div key={d.id} className="flex justify-between items-center bg-white border border-slate-100 p-3 mb-1" data-testid={`regatta-doc-row-${d.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-navy text-sm truncate">{d.race_number > 0 && <span className="text-xs text-club-yellow-dark mr-2">#{d.race_number}</span>}{d.title}</div>
                    <div className="text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString("fr")}</div>
                  </div>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api${d.url}`} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-widest text-navy border border-slate-300 px-2 py-1 mr-2 hover:bg-navy hover:text-white">PDF</a>
                  <button onClick={() => del(d.id)} className="text-red-600 hover:text-red-800" data-testid={`regatta-doc-del-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================== */
/* PROMO CODES TAB                                                 */
/* ============================================================== */
export function PromoCodesTab() {
  const [codes, setCodes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: "", discount_type: "percent", value: 10, max_uses: 0,
    expires_at: "", members_only: true, active: true,
  });
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/promo-codes"); setCodes(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/admin/promo-codes", {
        ...form,
        value: parseFloat(form.value),
        max_uses: parseInt(form.max_uses) || 0,
        expires_at: form.expires_at || null,
      });
      setShowForm(false);
      setForm({ code: "", discount_type: "percent", value: 10, max_uses: 0, expires_at: "", members_only: true, active: true });
      load();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };

  const toggleActive = async (id, active) => {
    const fd = new FormData();
    fd.append("active", !active);
    await api.patch(`/admin/promo-codes/${id}`, fd);
    load();
  };

  const del = async (id) => {
    if (!window.confirm(tr("Supprimer ce code promo ?"))) return;
    await api.delete(`/admin/promo-codes/${id}`);
    load();
  };

  return (
    <div data-testid="admin-promo-codes">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-serif text-2xl text-navy">Codes promo ({codes.length})</h3>
        <button onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red" data-testid="promo-add-toggle">
          <Plus className="w-4 h-4" />{tr("Nouveau code")}</button>
      </div>

      {showForm && (
        <form onSubmit={create} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="promo-form">
          <input required placeholder="Code (ex: MEMBRE2026)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="border border-slate-300 px-3 py-2 uppercase tracking-widest" data-testid="promo-code-input" />
          <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="promo-type">
            <option value="percent">Pourcentage (%)</option>
            <option value="fixed">Montant fixe (€)</option>
          </select>
          <input required type="number" step="0.01" placeholder={tr("Valeur")} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="promo-value" />
          <input type="number" placeholder={tr("Usages max (0 = illimité)")} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="promo-maxuses" />
          <input type="date" placeholder={tr("Expire le")} value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="promo-expires" />
          <label className="flex items-center gap-2 text-sm text-navy">
            <input type="checkbox" checked={form.members_only} onChange={(e) => setForm({ ...form, members_only: e.target.checked })} data-testid="promo-members-only" />
            Réservé aux membres
          </label>
          {err && <div className="md:col-span-2 text-red-600 text-sm" data-testid="promo-err">{err}</div>}
          <button type="submit" className="md:col-span-2 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="promo-submit">{tr("Créer")}</button>
        </form>
      )}

      {codes.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500">{tr("Aucun code.")}</div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy text-white text-left">
              <tr>{["Code", "Type", "Valeur", "Usages", "Expire", "Membres", "Actif", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{tr(h)}</th>
              ))}</tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.id} className="border-t border-slate-100" data-testid={`promo-row-${c.code}`}>
                  <td className="px-4 py-3 font-mono font-semibold text-navy">{c.code}</td>
                  <td className="px-4 py-3 text-xs uppercase tracking-widest">{c.discount_type}</td>
                  <td className="px-4 py-3">{c.discount_type === "percent" ? `${c.value}%` : `€ ${c.value.toFixed(2)}`}</td>
                  <td className="px-4 py-3">{c.uses} / {c.max_uses || "∞"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{c.expires_at ? c.expires_at.substring(0, 10) : "—"}</td>
                  <td className="px-4 py-3">{c.members_only ? <Check className="w-4 h-4 text-green-700" /> : "—"}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActive(c.id, c.active)} className={`text-[10px] uppercase tracking-widest px-2 py-1 ${c.active ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"}`} data-testid={`promo-toggle-${c.code}`}>
                      {c.active ? tr("Actif") : "Inactif"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => del(c.id)} className="text-red-600 hover:text-red-800" data-testid={`promo-del-${c.code}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============================================================== */
/* AGENDA / EVENTS TAB                                             */
/* ============================================================== */
export function AgendaTab() {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [docsEvent, setDocsEvent] = useState(null);
  const [volunteersEvent, setVolunteersEvent] = useState(null);
  const [form, setForm] = useState({
    title_fr: "", title_nl: "", title_en: "",
    description_fr: "", description_nl: "", description_en: "",
    date: "", location: "Club-house", featured_popup: false,
  });
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/events"); setEvents(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const reset = () => {
    setForm({ title_fr: "", title_nl: "", title_en: "", description_fr: "", description_nl: "", description_en: "", date: "", location: "Club-house", featured_popup: false });
    setEditing(null);
    setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      if (editing) {
        await api.patch(`/admin/events/${editing.id}`, form);
      } else {
        await api.post("/admin/events", form);
      }
      reset();
      load();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };

  const startEdit = (ev) => {
    setEditing(ev);
    setForm({
      title_fr: ev.title_fr || "", title_nl: ev.title_nl || "", title_en: ev.title_en || "",
      description_fr: ev.description_fr || "", description_nl: ev.description_nl || "", description_en: ev.description_en || "",
      date: ev.date || "", location: ev.location || "", featured_popup: !!ev.featured_popup,
    });
    setShowForm(true);
  };

  const del = async (id) => {
    if (!window.confirm(tr("Supprimer cet événement et ses documents ?"))) return;
    await api.delete(`/admin/events/${id}`);
    load();
  };

  return (
    <div data-testid="admin-agenda">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-serif text-2xl text-navy">Événements ({events.length})</h3>
        <button onClick={() => { setEditing(null); setShowForm(!showForm); }} className="inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red" data-testid="event-add-toggle">
          <Plus className="w-4 h-4" /> Nouvel événement
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="event-form">
          <div className="md:col-span-3 text-xs uppercase tracking-widest text-club-red font-semibold">
            {editing ? `Modifier — ${editing.title_fr}` : tr("Nouveau")}
          </div>
          <input required placeholder={tr("Titre FR")} value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="event-title-fr" />
          <input placeholder={tr("Titre NL")} value={form.title_nl} onChange={(e) => setForm({ ...form, title_nl: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="event-title-nl" />
          <input placeholder={tr("Titre EN")} value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="event-title-en" />
          <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="event-date" />
          <input placeholder={tr("Lieu")} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="event-location" />
          <textarea placeholder="Description FR" value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-3 min-h-20" data-testid="event-desc-fr" />
          <textarea placeholder="Description NL" value={form.description_nl} onChange={(e) => setForm({ ...form, description_nl: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="event-desc-nl" />
          <textarea placeholder="Description EN" value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="event-desc-en" />
          <label className="md:col-span-3 flex items-center gap-2 bg-club-yellow/20 border border-club-yellow px-3 py-2 cursor-pointer">
            <input type="checkbox" checked={form.featured_popup} onChange={(e) => setForm({ ...form, featured_popup: e.target.checked })} data-testid="event-featured" />
            <span className="text-sm text-navy"><b>Afficher en pop-up</b> sur la page d'accueil (le visiteur peut le fermer définitivement)</span>
          </label>
          {err && <div className="md:col-span-3 text-red-600 text-sm" data-testid="event-err">{err}</div>}
          <div className="md:col-span-3 flex gap-3">
            <button type="submit" className="flex-1 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="event-submit">
              {editing ? tr("Mettre à jour") : tr("Créer")}
            </button>
            <button type="button" onClick={reset} className="px-5 bg-slate-200 text-navy py-3 text-xs uppercase tracking-[0.2em] hover:bg-slate-300" data-testid="event-cancel">{tr("Annuler")}</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.map((ev) => (
          <div key={ev.id} className="bg-white border border-slate-200 p-5" data-testid={`admin-event-${ev.id}`}>
            <CalendarDays className="w-6 h-6 text-club-red mb-3" strokeWidth={1.5} />
            <div className="font-serif text-lg text-navy">{ev.title_fr}</div>
            <div className="text-xs text-slate-500 mt-1">{ev.date} · {ev.location}</div>
            {ev.description_fr && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{ev.description_fr}</p>}
            <div className="flex gap-2 mt-4 flex-wrap">
              <button onClick={() => startEdit(ev)} className="text-[11px] uppercase tracking-widest text-navy border border-navy px-3 py-1.5 hover:bg-navy hover:text-white" data-testid={`event-edit-${ev.id}`}>{tr("Modifier")}</button>
              <button onClick={() => setDocsEvent(ev)} className="text-[11px] uppercase tracking-widest text-club-red border border-club-red px-3 py-1.5 hover:bg-club-red hover:text-white" data-testid={`event-docs-${ev.id}`}>
                <FileText className="w-3 h-3 inline mr-1" /> Documents
              </button>
              <button onClick={() => setVolunteersEvent(ev)} className="text-[11px] uppercase tracking-widest text-emerald-700 border border-emerald-700 px-3 py-1.5 hover:bg-emerald-700 hover:text-white" data-testid={`event-volunteers-${ev.id}`}>
                Bénévoles
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm(`Envoyer le rappel 48 h pour « ${ev.title_fr} » maintenant ?\n(Email aux bénévoles + notification in-app à tous les membres actifs)`)) return;
                  try {
                    const { data } = await api.post(`/admin/events/${ev.id}/send-reminder`);
                    toast.success(`${tr("Rappel envoyé —")} ${data.recipients} ${tr("destinataires")} (${data.volunteers || 0} ${tr("bénévole·s")})`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || tr("Erreur lors de l'envoi"));
                  }
                }}
                className="text-[11px] uppercase tracking-widest text-amber-700 border border-amber-700 px-3 py-1.5 hover:bg-amber-700 hover:text-white"
                data-testid={`event-remind-${ev.id}`}
                title={tr("Envoyer le rappel 48 h maintenant")}
              >
                <BellRing className="w-3 h-3 inline mr-1" /> Rappel
              </button>
              <button onClick={() => del(ev.id)} className="ml-auto text-[11px] uppercase tracking-widest text-red-600 hover:underline" data-testid={`event-del-${ev.id}`}>{tr("Supprimer")}</button>
            </div>
          </div>
        ))}
      </div>

      {docsEvent && <EventDocsModal event={docsEvent} onClose={() => setDocsEvent(null)} />}
      {volunteersEvent && <EventVolunteersModal event={volunteersEvent} onClose={() => setVolunteersEvent(null)} />}
    </div>
  );
}

function EventDocsModal({ event, onClose }) {
  const [docs, setDocs] = useState([]);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("notice");
  const [file, setFile] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get(`/events/${event.id}/documents`); setDocs(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [event.id]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { setErr(tr("Sélectionnez un PDF")); return; }
    setErr("");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || file.name);
    fd.append("doc_type", docType);
    try {
      await api.post(`/admin/events/${event.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null); setTitle("");
      load();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur upload"); }
  };

  const del = async (id) => {
    if (!window.confirm(tr("Supprimer ce document ?"))) return;
    await api.delete(`/admin/events/${event.id}/documents/${id}`);
    load();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="event-docs-modal" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full p-8 relative my-12" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="event-docs-close">
          <X className="w-5 h-5" />
        </button>
        <div className="label-eyebrow text-club-red mb-2">{tr("Documents de l'événement")}</div>
        <h2 className="font-serif text-2xl text-navy">{event.title_fr}</h2>
        <div className="text-xs text-slate-500 mt-1">{event.date} · {event.location}</div>
        <div className="divider-gold mt-4 mb-6" />

        <form onSubmit={upload} className="bg-sand border border-slate-200 p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="event-upload-form">
          <select value={docType} onChange={(e) => setDocType(e.target.value)} className="border border-slate-300 px-3 py-2" data-testid="event-upload-type">
            <option value="notice">Avis / Communication</option>
            <option value="program">Programme</option>
            <option value="other">{tr("Autre")}</option>
          </select>
          <input placeholder={tr("Titre")} value={title} onChange={(e) => setTitle(e.target.value)} className="border border-slate-300 px-3 py-2" data-testid="event-upload-title" />
          <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])} className="md:col-span-2 text-sm" data-testid="event-upload-file" />
          {err && <div className="md:col-span-2 text-red-600 text-sm">{err}</div>}
          <button type="submit" className="md:col-span-2 bg-club-red text-white py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="event-upload-submit">
            <Upload className="w-3.5 h-3.5 inline mr-2" />{tr("Téléverser")}</button>
        </form>

        {docs.length === 0 ? (
          <div className="text-slate-400 text-sm border border-dashed border-slate-300 p-6 text-center">{tr("Aucun document.")}</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {docs.map((d) => (
              <li key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid={`event-doc-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-navy truncate">{d.title}</div>
                  <div className="text-xs text-slate-500">{d.doc_type} · {new Date(d.created_at).toLocaleDateString("fr")}</div>
                </div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api${d.url}`} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-widest text-navy border border-slate-300 px-2 py-1 hover:bg-navy hover:text-white">PDF</a>
                <button onClick={() => del(d.id)} className="text-red-600 hover:text-red-800" data-testid={`event-doc-del-${d.id}`}><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ============================================================== */
/* EVENT VOLUNTEERS MODAL (admin/president manage tasks + signups) */
/* ============================================================== */
function EventVolunteersModal({ event, onClose }) {
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", slots_needed: 1, start_time: "", notes: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/admin/events/${event.id}/tasks`); setTasks(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [event.id]);

  const create = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post(`/admin/events/${event.id}/tasks`, {
        ...form, slots_needed: parseInt(form.slots_needed) || 1,
      });
      setForm({ title: "", description: "", slots_needed: 1, start_time: "", notes: "" });
      load();
    } catch (e2) { setErr(e2.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const delTask = async (id) => {
    if (!window.confirm(tr("Supprimer cette mission et tous les bénévoles inscrits ?"))) return;
    await api.delete(`/admin/events/${event.id}/tasks/${id}`); load();
  };
  const updateTask = async (taskId, patch) => {
    await api.patch(`/admin/events/${event.id}/tasks/${taskId}`, patch);
    load();
  };
  const delVolunteer = async (id) => {
    if (!window.confirm(tr("Retirer ce bénévole ?"))) return;
    await api.delete(`/admin/events/${event.id}/volunteers/${id}`); load();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="event-volunteers-modal" onClick={onClose}>
      <div className="bg-white max-w-4xl w-full p-6 md:p-8 relative my-12" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="event-volunteers-close"><X className="w-5 h-5" /></button>
        <div className="label-eyebrow text-emerald-700 mb-2">{tr("Missions & bénévoles")}</div>
        <h2 className="font-serif text-2xl text-navy">{event.title_fr}</h2>
        <div className="text-xs text-slate-500 mt-1">{event.date} · {event.location}</div>
        <div className="divider-gold mt-4 mb-6" />

        <form onSubmit={create} className="bg-sand border border-slate-200 p-4 md:p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="task-create-form">
          <div className="md:col-span-2 text-xs uppercase tracking-widest text-emerald-700 font-semibold">{tr("Créer une nouvelle mission")}</div>
          <input required placeholder={tr("Titre (ex: Accueil & inscription)")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="task-title" />
          <input required type="number" min="1" placeholder={tr("Places à pourvoir")} value={form.slots_needed} onChange={(e) => setForm({ ...form, slots_needed: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="task-slots" />
          <input placeholder="Horaire (ex: 9h-12h)" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="task-time" />
          <textarea placeholder="Description / consignes" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="task-desc" />
          {err && <div className="md:col-span-2 text-red-600 text-sm" data-testid="task-err">{err}</div>}
          <button type="submit" disabled={busy} className="md:col-span-2 bg-emerald-700 text-white py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-emerald-800 disabled:opacity-60" data-testid="task-create-submit">
            + Ajouter la mission
          </button>
        </form>

        {tasks.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 p-8 text-center text-slate-500" data-testid="tasks-empty">{tr("Aucune mission créée pour cet événement.")}</div>
        ) : (
          <div className="space-y-4">
            {tasks.map((tk) => (
              <TaskCard
                key={tk.id} tk={tk}
                onDelete={() => delTask(tk.id)}
                onSave={(patch) => updateTask(tk.id, patch)}
                onDeleteVolunteer={delVolunteer}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskCard({ tk, onDelete, onSave, onDeleteVolunteer }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    title: tk.title || "", description: tk.description || "",
    slots_needed: tk.slots_needed || 1, start_time: tk.start_time || "", notes: tk.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await onSave({ ...form, slots_needed: parseInt(form.slots_needed) || 1 });
      setEdit(false);
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200" data-testid={`task-${tk.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 p-4 border-b border-slate-100">
        {!edit ? (
          <>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-serif text-lg text-navy">{tk.title}</div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${tk.slots_left === 0 ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>
                  {tk.slots_taken}/{tk.slots_needed} {tk.slots_left === 0 ? "complet" : "places"}
                </span>
              </div>
              {tk.start_time && <div className="text-xs text-slate-500 mt-1">⏰ {tk.start_time}</div>}
              {tk.description && <p className="text-sm text-slate-600 mt-1">{tk.description}</p>}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setEdit(true)} className="text-[10px] uppercase tracking-widest text-navy hover:underline" data-testid={`task-edit-${tk.id}`}>{tr("Éditer")}</button>
              <button onClick={onDelete} className="text-[10px] uppercase tracking-widest text-red-600 hover:underline" data-testid={`task-del-${tk.id}`}>{tr("Supprimer")}</button>
            </div>
          </>
        ) : (
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-2">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={tr("Titre")} className="border border-slate-300 px-2 py-1.5 text-sm md:col-span-2" data-testid={`task-edit-title-${tk.id}`} />
            <input type="number" min={Math.max(1, tk.slots_taken)} value={form.slots_needed} onChange={(e) => setForm({ ...form, slots_needed: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm" placeholder="Places" />
            <input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm" placeholder="Horaire" />
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm md:col-span-2" placeholder={tr("Description")} />
            <div className="md:col-span-2 flex gap-2">
              <button onClick={save} disabled={busy} className="bg-emerald-700 text-white px-4 py-1.5 text-[10px] uppercase tracking-widest hover:bg-emerald-800 disabled:opacity-60" data-testid={`task-save-${tk.id}`}>
                {busy ? "…" : tr("Enregistrer")}
              </button>
              <button onClick={() => setEdit(false)} className="border border-slate-300 px-4 py-1.5 text-[10px] uppercase tracking-widest hover:bg-slate-100">{tr("Annuler")}</button>
            </div>
          </div>
        )}
      </div>
      {tk.volunteers.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 font-medium">{tr("Bénévole")}</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 font-medium">{tr("Contact")}</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 font-medium">Notes</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tk.volunteers.map((v) => (
                <tr key={v.id} className="border-t border-slate-100" data-testid={`volunteer-${v.id}`}>
                  <td className="px-3 py-2 text-navy">{v.name}</td>
                  <td className="px-3 py-2 text-slate-600">{v.email}<br /><span className="text-xs text-slate-400">{v.phone || ""}</span></td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{v.notes || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onDeleteVolunteer(v.id)} className="text-[10px] uppercase tracking-widest text-red-600 hover:underline" data-testid={`volunteer-del-${v.id}`}>Retirer</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



/* ============================================================== */
/* CLUB DUES TAB (cotisations)                                     */
/* ============================================================== */
export function ClubDuesTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name_fr: "", name_nl: "", name_en: "",
    price: "", note_fr: "", note_nl: "", note_en: "",
    order: 0,
  });
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/club-dues"); setItems(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const reset = () => {
    setForm({ name_fr: "", name_nl: "", name_en: "", price: "", note_fr: "", note_nl: "", note_en: "", order: 0 });
    setEditing(null); setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing) {
        await api.patch(`/admin/club-dues/${editing.id}`, payload);
      } else {
        await api.post("/admin/club-dues", payload);
      }
      reset(); load();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };

  const startEdit = (it) => {
    setEditing(it);
    setForm({
      name_fr: it.name_fr || "", name_nl: it.name_nl || "", name_en: it.name_en || "",
      price: it.price || "", note_fr: it.note_fr || "", note_nl: it.note_nl || "", note_en: it.note_en || "",
      order: it.order || 0,
    });
    setShowForm(true);
  };

  const del = async (id) => {
    if (!window.confirm(tr("Supprimer cette cotisation ?"))) return;
    await api.delete(`/admin/club-dues/${id}`);
    load();
  };

  return (
    <div data-testid="admin-club-dues">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-serif text-2xl text-navy">{tr("Cotisations affichées sur /club")} ({items.length})</h3>
        <button onClick={() => { setEditing(null); setShowForm(!showForm); }} className="inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red" data-testid="dues-add-toggle">
          <Plus className="w-4 h-4" />{tr("Nouvelle cotisation")}</button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="dues-form">
          <div className="md:col-span-3 text-xs uppercase tracking-widest text-club-red font-semibold">
            {editing ? `${tr("Modifier")} — ${editing.name_fr}` : tr("Nouvelle")}
          </div>
          <input required placeholder={tr("Nom FR")} value={form.name_fr} onChange={(e) => setForm({ ...form, name_fr: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="dues-name-fr" />
          <input placeholder={tr("Nom NL")} value={form.name_nl} onChange={(e) => setForm({ ...form, name_nl: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="dues-name-nl" />
          <input placeholder={tr("Nom EN")} value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="dues-name-en" />
          <input required placeholder='Prix (ex: "350 €")' value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-1" data-testid="dues-price" />
          <input type="number" placeholder="Ordre d'affichage" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="dues-order" />
          <input placeholder={tr("Note FR")} value={form.note_fr} onChange={(e) => setForm({ ...form, note_fr: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="dues-note-fr" />
          <input placeholder="Note NL" value={form.note_nl} onChange={(e) => setForm({ ...form, note_nl: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="dues-note-nl" />
          <input placeholder="Note EN" value={form.note_en} onChange={(e) => setForm({ ...form, note_en: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="dues-note-en" />
          {err && <div className="md:col-span-3 text-red-600 text-sm" data-testid="dues-err">{err}</div>}
          <div className="md:col-span-3 flex gap-3">
            <button type="submit" className="flex-1 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="dues-submit">
              {editing ? tr("Mettre à jour") : tr("Créer")}
            </button>
            <button type="button" onClick={reset} className="px-5 bg-slate-200 text-navy py-3 text-xs uppercase tracking-[0.2em] hover:bg-slate-300" data-testid="dues-cancel">{tr("Annuler")}</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-navy text-white text-left">
            <tr>{["Ordre", "Nom FR", "Prix", "Note FR", ""].map((h) => (
              <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{tr(h)}</th>
            ))}</tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`dues-row-${it.id}`}>
                <td className="px-4 py-3 text-slate-500">{it.order}</td>
                <td className="px-4 py-3 text-navy font-medium">{it.name_fr}</td>
                <td className="px-4 py-3 font-serif text-club-red font-semibold">{it.price}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{it.note_fr}</td>
                <td className="px-4 py-3 text-right flex gap-2 justify-end">
                  <button onClick={() => startEdit(it)} className="text-xs uppercase tracking-widest text-navy hover:underline" data-testid={`dues-edit-${it.id}`}>{tr("Modifier")}</button>
                  <button onClick={() => del(it.id)} className="text-red-600 hover:text-red-800" data-testid={`dues-del-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================== */
/* SITE SETTINGS TAB (footer)                                      */
/* ============================================================== */
export function SiteSettingsTab() {
  const [form, setForm] = useState({
    address_fr: "", address_nl: "", address_en: "",
    phone: "", email: "",
    hours_fr: "", hours_nl: "", hours_en: "",
    iban: "", bic: "", bank_holder: "",
    facebook_url: "", instagram_url: "", youtube_url: "",
    tagline_fr: "", tagline_nl: "", tagline_en: "",
  });
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/site-settings");
      setForm((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v ?? ""])) }));
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaved(false);
    try {
      await api.put("/admin/site-settings", form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };

  const sections = [
    {
      title: tr("Adresse"),
      fields: [
        { key: "address_fr", label: tr("Adresse FR") },
        { key: "address_nl", label: tr("Adresse NL") },
        { key: "address_en", label: tr("Adresse EN") },
      ],
    },
    {
      title: "Contact",
      fields: [
        { key: "phone", label: tr("Téléphone")},
        { key: "email", label: tr("Email")},
      ],
    },
    {
      title: tr("Horaires d'ouverture"),
      fields: [
        { key: "hours_fr", label: "FR" },
        { key: "hours_nl", label: "NL" },
        { key: "hours_en", label: "EN" },
      ],
    },
    {
      title: tr("Coordonnées bancaires"),
      fields: [
        { key: "iban", label: "IBAN" },
        { key: "bic",  label: "BIC" },
        { key: "bank_holder", label: tr("Titulaire") },
      ],
    },
    {
      title: tr("Réseaux sociaux"),
      fields: [
        { key: "facebook_url",  label: "Facebook URL" },
        { key: "instagram_url", label: "Instagram URL" },
        { key: "youtube_url",   label: "YouTube URL" },
      ],
    },
    {
      title: "Tagline (slogan footer)",
      fields: [
        { key: "tagline_fr", label: "FR" },
        { key: "tagline_nl", label: "NL" },
        { key: "tagline_en", label: "EN" },
      ],
    },
  ];

  return (
    <div data-testid="admin-site-settings">
      <h3 className="font-serif text-2xl text-navy mb-2">{tr("Pied de page & informations du site")}</h3>
      <p className="text-sm text-slate-500 mb-6">{tr("Modifications appliquées immédiatement sur toutes les pages.")}</p>

      <form onSubmit={submit} className="space-y-6 max-w-4xl" data-testid="settings-form">
        {sections.map((sec) => (
          <div key={sec.title} className="bg-white border border-slate-200 p-6">
            <div className="label-eyebrow mb-4 text-club-red">{sec.title}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sec.fields.map((f) => (
                <label key={f.key} className="block">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{f.label}</div>
                  <input
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full border border-slate-300 px-3 py-2"
                    data-testid={`settings-${f.key}`}
                  />
                </label>
              ))}
            </div>
          </div>
        ))}

        {err && <div className="text-red-600 text-sm" data-testid="settings-err">{err}</div>}
        {saved && <div className="text-green-700 text-sm flex items-center gap-2" data-testid="settings-saved"><Check className="w-4 h-4" /> Enregistré</div>}

        <button type="submit" className="bg-club-red text-white px-8 py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red-dark" data-testid="settings-submit">{tr("Enregistrer les modifications")}</button>
      </form>
    </div>
  );
}

/* ============================================================== */
/* LEGAL PAGES TAB                                                 */
/* ============================================================== */
const LEGAL_SLUGS = [
  { slug: "statuts", label: "Statuts de l'ASBL" },
  { slug: "roi",     label: "Règlement d'Ordre Intérieur (ROI)" },
  { slug: "privacy", label: "Politique de vie privée" },
  { slug: "gdpr",    label: "Protection des données (RGPD)" },
];

const EMPTY_LEGAL_FORM = { title_fr: "", title_nl: "", title_en: "", content_fr: "", content_nl: "", content_en: "" };

export function LegalPagesTab() {
  const [active, setActive] = useState("statuts");
  const [form, setForm] = useState(EMPTY_LEGAL_FORM);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState("");
  const [files, setFiles] = useState({});
  const [fileBusy, setFileBusy] = useState("");
  const [fileErr, setFileErr] = useState("");
  const activeRef = useRef("statuts");

  const setField = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };
  // Quill fires programmatic onChange (source "api") when value is injected — only user edits count.
  const setQuillField = (field) => (value, _delta, source) => {
    if (source !== "user") return;
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const switchTab = (slug) => {
    if (slug === active) return;
    if (dirty && !window.confirm(tr("Vous avez des modifications non enregistrées. Voulez-vous vraiment changer de page ?"))) return;
    setActive(slug);
  };

  useEffect(() => {
    activeRef.current = active;
    setSaved(false); setErr(""); setLoading(true); setDirty(false);
    setForm(EMPTY_LEGAL_FORM);
    setFiles({}); setFileErr("");
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/legal-pages/${active}`);
        if (cancelled || activeRef.current !== active) return;
        setForm({
          title_fr: data.title_fr || "", title_nl: data.title_nl || "", title_en: data.title_en || "",
          content_fr: data.content_fr || "", content_nl: data.content_nl || "", content_en: data.content_en || "",
        });
        setFiles(data.files || {});
      } catch { /* document absent : formulaire vide */ }
      if (!cancelled && activeRef.current === active) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [active]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaved(false);
    const slug = active;
    // Quill 2 serializes every space as &nbsp; — normalize to keep HTML clean and wrappable.
    const clean = (h) => (h || "").replace(/&nbsp;/g, " ").replace(/\u00a0/g, " ");
    const payload = {
      ...form,
      content_fr: clean(form.content_fr),
      content_nl: clean(form.content_nl),
      content_en: clean(form.content_en),
    };
    try {
      await api.put(`/admin/legal-pages/${slug}`, payload);
      if (activeRef.current === slug) {
        setDirty(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      }
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };

  const uploadFile = async (lg, f) => {
    if (!f) return;
    const slug = active;
    setFileErr(""); setFileBusy(lg);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("lang", lg);
    try {
      const { data } = await api.post(`/admin/legal-pages/${slug}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (activeRef.current === slug) setFiles((prev) => ({ ...prev, [lg]: data.file }));
    } catch (e) {
      if (activeRef.current === slug) setFileErr(e.response?.data?.detail || tr("Erreur lors de l'upload"));
    } finally {
      if (activeRef.current === slug) setFileBusy("");
    }
  };

  const deleteFile = async (lg) => {
    if (!window.confirm(tr("Supprimer ce fichier ?"))) return;
    const slug = active;
    setFileErr(""); setFileBusy(lg);
    try {
      await api.delete(`/admin/legal-pages/${slug}/file/${lg}`);
      if (activeRef.current === slug) setFiles((prev) => { const n = { ...prev }; delete n[lg]; return n; });
    } catch (e) {
      if (activeRef.current === slug) setFileErr(e.response?.data?.detail || "Erreur");
    } finally {
      if (activeRef.current === slug) setFileBusy("");
    }
  };

  return (
    <div data-testid="admin-legal-pages">
      <h3 className="font-serif text-2xl text-navy mb-2">{tr("Pages légales (footer)")}</h3>
      <p className="text-sm text-slate-500 mb-6">{tr("Modifiez le contenu des 4 documents accessibles depuis le pied de page.")}</p>

      <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-6" data-testid="legal-page-tabs">
        {LEGAL_SLUGS.map((s) => (
          <button
            key={s.slug} type="button" onClick={() => switchTab(s.slug)}
            className={`px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 ${
              active === s.slug ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"
            }`}
            data-testid={`legal-tab-${s.slug}`}
            aria-selected={active === s.slug}
          >
            {tr(s.label)}
            {active === s.slug && dirty && <span className="ml-1 text-club-red" title={tr("Modifications non enregistrées")}>•</span>}
          </button>
        ))}
      </div>

      <form key={active} onSubmit={submit} className={`space-y-6 max-w-4xl ${loading ? "opacity-50 pointer-events-none" : ""}`} data-testid="legal-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Titre FR")}</div>
            <input value={form.title_fr} onChange={(e) => setField("title_fr")(e.target.value)} className="w-full border border-slate-300 px-3 py-2" data-testid="legal-title-fr" />
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Titre NL")}</div>
            <input value={form.title_nl} onChange={(e) => setField("title_nl")(e.target.value)} className="w-full border border-slate-300 px-3 py-2" data-testid="legal-title-nl" />
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Titre EN")}</div>
            <input value={form.title_en} onChange={(e) => setField("title_en")(e.target.value)} className="w-full border border-slate-300 px-3 py-2" data-testid="legal-title-en" />
          </label>
        </div>

        <label className="block">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Contenu FR")}</div>
          <div data-testid="legal-content-fr">
            <ReactQuill theme="snow" value={form.content_fr} onChange={setQuillField("content_fr")} modules={QUILL_MODULES} className="bg-white" />
          </div>
        </label>
        <label className="block">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Contenu NL")}</div>
          <div data-testid="legal-content-nl">
            <ReactQuill theme="snow" value={form.content_nl} onChange={setQuillField("content_nl")} modules={QUILL_MODULES} className="bg-white" />
          </div>
        </label>
        <label className="block">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Contenu EN")}</div>
          <div data-testid="legal-content-en">
            <ReactQuill theme="snow" value={form.content_en} onChange={setQuillField("content_en")} modules={QUILL_MODULES} className="bg-white" />
          </div>
        </label>

        {err && <div className="text-red-600 text-sm" data-testid="legal-err">{err}</div>}
        {saved && <div className="text-green-700 text-sm flex items-center gap-2" data-testid="legal-saved"><Check className="w-4 h-4" /> {tr("Enregistré")}</div>}

        <button type="submit" disabled={loading} className="bg-club-red text-white px-8 py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red-dark disabled:opacity-50" data-testid="legal-submit">{tr("Enregistrer")}</button>
      </form>

      <div className="mt-10 max-w-4xl border-t border-slate-200 pt-8" data-testid="legal-files-section">
        <h4 className="font-serif text-xl text-navy mb-1">{tr("Document officiel téléchargeable")}</h4>
        <p className="text-sm text-slate-500 mb-4">{tr("PDF recommandé (DOC/DOCX acceptés, max 10 MB). Le bouton de téléchargement apparaît sur la page publique si un fichier est attaché.")}</p>
        {fileErr && <div className="text-red-600 text-sm mb-3" data-testid="legal-file-err">{fileErr}</div>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {["fr", "nl", "en"].map((lg) => {
            const f = files[lg];
            return (
              <div key={`${active}-${lg}`} className="border border-slate-200 p-4 bg-slate-50" data-testid={`legal-file-${lg}`}>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">{lg.toUpperCase()}</div>
                {f ? (
                  <div className="space-y-2">
                    <div className="text-sm text-navy break-all flex items-start gap-1.5" data-testid={`legal-file-name-${lg}`}>
                      <FileText className="w-4 h-4 mt-0.5 shrink-0 text-club-red" /> {f.name}
                    </div>
                    <div className="text-xs text-slate-500" data-testid={`legal-file-date-${lg}`}>
                      {tr("Mis à jour le")} {new Date(f.uploaded_at).toLocaleDateString()} · {(f.size / 1024).toFixed(0)} Ko
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api${f.url}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-xs uppercase tracking-widest text-club-red underline underline-offset-2 hover:text-navy"
                        data-testid={`legal-file-download-${lg}`}
                      >{tr("Télécharger")}</a>
                      <label className="text-xs uppercase tracking-widest text-slate-600 underline underline-offset-2 hover:text-navy cursor-pointer">
                        {fileBusy === lg ? tr("Envoi…") : tr("Remplacer")}
                        <input type="file" accept=".pdf,.doc,.docx" className="hidden" disabled={!!fileBusy}
                          onChange={(e) => { uploadFile(lg, e.target.files?.[0]); e.target.value = ""; }}
                          data-testid={`legal-file-replace-${lg}`} />
                      </label>
                      <button type="button" onClick={() => deleteFile(lg)} disabled={!!fileBusy}
                        className="text-xs uppercase tracking-widest text-red-600 underline underline-offset-2 hover:text-red-800"
                        data-testid={`legal-file-delete-${lg}`}>{tr("Supprimer")}</button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-sm text-slate-400" data-testid={`legal-file-empty-${lg}`}>{tr("Aucun fichier")}</div>
                    <label className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-club-red cursor-pointer">
                      <Upload className="w-3.5 h-3.5" /> {fileBusy === lg ? tr("Envoi…") : tr("Téléverser")}
                      <input type="file" accept=".pdf,.doc,.docx" className="hidden" disabled={!!fileBusy}
                        onChange={(e) => { uploadFile(lg, e.target.files?.[0]); e.target.value = ""; }}
                        data-testid={`legal-file-input-${lg}`} />
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ============================================================== */
/* ADMISSION FORM TAB                                              */
/* ============================================================== */
export function AdmissionFormTab() {
  const [pdfs, setPdfs] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/pdfs", { params: { category: "admission" } });
      setPdfs(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { setErr(tr("Sélectionnez un fichier PDF")); return; }
    setErr(""); setInfo(""); setSaving(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || file.name);
    fd.append("category", "admission");
    try {
      await api.post("/admin/pdfs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInfo(tr("Formulaire publié — désormais téléchargeable sur /club#admission"));
      setTitle(""); setFile(null);
      // reset the file input visually
      document.querySelectorAll('[data-testid="admission-upload-file"]').forEach((el) => { el.value = ""; });
      load();
      setTimeout(() => setInfo(""), 4000);
    } catch (e) {
      setErr(e.response?.data?.detail || tr("Erreur lors de l'upload"));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm(tr("Supprimer ce formulaire ? Il ne sera plus téléchargeable depuis le site public."))) return;
    await api.delete(`/admin/pdfs/${id}`);
    load();
  };

  return (
    <div data-testid="admin-admission-form">
      <h3 className="font-serif text-2xl text-navy mb-2">{tr("Formulaire de demande d'admission")}</h3>
      <p className="text-sm text-slate-500 mb-6 max-w-2xl">
        Uploadez ici le formulaire PDF que les futurs membres pourront télécharger depuis la page
        <span className="font-mono text-club-red"> /club#admission </span>
        pour compléter leur dossier. Vous pouvez publier plusieurs versions (ex. édition 2026, mineurs, etc.).
      </p>

      <form onSubmit={upload} className="bg-white border border-slate-200 p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admission-upload-form">
        <div className="md:col-span-2 label-eyebrow text-club-red">{tr("Nouveau formulaire")}</div>
        <label className="block md:col-span-2">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Titre affiché sur le site")}</div>
          <input
            placeholder="Ex: Demande d'admission — saison 2026"
            value={title} onChange={(e) => setTitle(e.target.value)}
            className="w-full border border-slate-300 px-3 py-2"
            data-testid="admission-upload-title"
          />
        </label>
        <label className="block md:col-span-2">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">Fichier PDF</div>
          <input
            type="file" accept="application/pdf" required
            onChange={(e) => setFile(e.target.files[0])}
            className="block w-full text-sm"
            data-testid="admission-upload-file"
          />
        </label>
        {err && <div className="md:col-span-2 text-red-600 text-sm" data-testid="admission-upload-err">{err}</div>}
        {info && (
          <div className="md:col-span-2 text-green-700 text-sm flex items-center gap-2" data-testid="admission-upload-info">
            <Check className="w-4 h-4" /> {info}
          </div>
        )}
        <button
          type="submit" disabled={saving}
          className="md:col-span-2 bg-club-red text-white py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red-dark disabled:opacity-60"
          data-testid="admission-upload-submit"
        >
          <Upload className="w-3.5 h-3.5 inline mr-2" /> {saving ? tr("Envoi…") : tr("Publier le formulaire")}
        </button>
      </form>

      <h4 className="font-serif text-lg text-navy mb-3">Formulaires actuellement disponibles ({pdfs.length})</h4>
      <div className="divider-gold mb-4" />

      {pdfs.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="admission-list-empty">
          Aucun formulaire publié. Le bouton de téléchargement sur <span className="font-mono">/club#admission</span> n'apparaîtra pas tant qu'aucun PDF n'est uploadé.
        </div>
      ) : (
        <ul className="bg-white border border-slate-200 divide-y divide-slate-100">
          {pdfs.map((p) => (
            <li key={p.id} className="p-4 flex items-center justify-between gap-3" data-testid={`admission-pdf-row-${p.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-5 h-5 text-club-red shrink-0" strokeWidth={1.5} />
                <div className="min-w-0">
                  <div className="text-navy text-sm truncate">{p.title}</div>
                  <div className="text-xs text-slate-500">Publié le {new Date(p.created_at).toLocaleDateString("fr")}</div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api${p.url}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-xs uppercase tracking-widest text-navy border border-navy px-3 py-1.5 hover:bg-navy hover:text-white"
                  data-testid={`admission-pdf-view-${p.id}`}
                >{tr("Voir")}</a>
                <button
                  onClick={() => del(p.id)}
                  className="text-red-600 hover:text-red-800"
                  data-testid={`admission-pdf-del-${p.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================== */
/* ANNOUNCEMENTS / NOTIFICATIONS TAB                               */
/* ============================================================== */
const ANN_TYPES = [
  { v: "notification",  label: "Notification interne" },
  { v: "annonce",       label: "Annonce" },
  { v: "convocation",   label: "Convocation" },
  { v: "regate_info",   label: tr("Information régate")},
  { v: "modification",  label: tr("Modification d'activité")},
  { v: "alerte_meteo",  label: tr("Alerte météo/navigation")},
  { v: "newsletter",    label: "Newsletter" },
];

const FIXED_AUDIENCES = [
  { v: "all",         label: tr("Tous les membres")},
  { v: "board",       label: tr("Conseil d'administration")},
  { v: "instructors", label: "Moniteurs" },
  { v: "sailing",     label: "Membres voile" },
  { v: "motor",       label: "Membres moteur" },
];

export function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState([]);
  const [regattas, setRegattas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    type: "annonce", audience: "all",
    title_fr: "", title_nl: "", title_en: "",
    body_fr: "", body_nl: "", body_en: "",
    send_email: false,
  });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/announcements"); setItems(data); } catch { /* ignore */ }
  };
  const loadOpts = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/products", { params: { type: "stage" } }).catch(() => ({ data: [] })),
        api.get("/regattas"),
      ]);
      setStages(s.data || []); setRegattas(r.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); loadOpts(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setInfo(""); setSending(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/admin/announcements", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const emailLine = data.email_result
        ? ` · Email : ${data.email_result.status} (${data.email_result.sent || 0} envoyé${data.email_result.failed?.length ? `, ${data.email_result.failed.length} échec` : ""})`
        : "";
      setInfo(`Annonce envoyée à ${data.recipients_count} destinataire(s).${emailLine}`);
      setForm({ ...form, title_fr: "", title_nl: "", title_en: "", body_fr: "", body_nl: "", body_en: "", send_email: false });
      setFiles([]);
      setShowForm(false);
      load();
      setTimeout(() => setInfo(""), 8000);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur");
    } finally { setSending(false); }
  };

  const del = async (id) => {
    if (!window.confirm(tr("Supprimer cette annonce ? (les destinataires perdront leur notification)"))) return;
    await api.delete(`/admin/announcements/${id}`);
    load();
  };

  const audienceLabel = (a) => {
    if (a.startsWith("stage:")) {
      const sid = a.split(":")[1];
      const s = stages.find((x) => x.id === sid);
      return `Stage : ${s ? (s.name_fr || s.title_fr || s.title) : sid.substring(0, 8)}`;
    }
    if (a.startsWith("regatta:")) {
      const sl = a.split(":")[1];
      const r = regattas.find((x) => x.slug === sl);
      return `Régate : ${r ? r.name : sl}`;
    }
    return FIXED_AUDIENCES.find((x) => x.v === a)?.label || a;
  };

  return (
    <div data-testid="admin-announcements">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="font-serif text-2xl text-navy">{tr("Notifications & Annonces")}</h3>
          <p className="text-sm text-slate-500 mt-1">{tr("Communications internes : in-app (cloche 🔔) + email (Resend).")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={async () => {
              if (!window.confirm(tr("Envoyer un rappel à tous les inscrits dont le stage commence dans 24-72h ?"))) return;
              try {
                const { data } = await api.post("/admin/notifications/trigger/stage-reminders");
                setInfo(`Rappels envoyés pour ${data.count} stage(s).`);
                setTimeout(() => setInfo(""), 6000);
                load();
              } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
            }}
            className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red"
            data-testid="trigger-stage-reminders"
          >{tr("Rappels stages 48h")}</button>
          <button onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-2 bg-club-red text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="ann-new-toggle">
            <Plus className="w-4 h-4" />{tr("Nouvelle annonce")}</button>
        </div>
      </div>

      {info && <div className="mb-4 bg-green-50 border border-green-300 text-green-900 p-4 text-sm flex items-start gap-2" data-testid="ann-info"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {info}</div>}

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 mb-6 space-y-4" data-testid="ann-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Type")}</div>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="ann-type">
                {ANN_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">Destinataires</div>
              <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="ann-audience">
                {FIXED_AUDIENCES.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
                <optgroup label={tr("Stages")}>
                  {stages.map((s) => <option key={s.id} value={`stage:${s.id}`}>Stage : {s.name_fr || s.title_fr || s.title || s.id.substring(0, 8)}</option>)}
                </optgroup>
                <optgroup label={tr("Régates")}>
                  {regattas.map((r) => <option key={r.slug} value={`regatta:${r.slug}`}>Régate : {r.name}</option>)}
                </optgroup>
              </select>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input required placeholder={tr("Titre FR")} value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="ann-title-fr" />
            <input placeholder={tr("Titre NL")} value={form.title_nl} onChange={(e) => setForm({ ...form, title_nl: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="ann-title-nl" />
            <input placeholder={tr("Titre EN")} value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="ann-title-en" />
          </div>

          <textarea required rows={5} placeholder="Message FR" value={form.body_fr} onChange={(e) => setForm({ ...form, body_fr: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="ann-body-fr" />
          <details className="text-sm text-slate-500">
            <summary className="cursor-pointer">Versions NL/EN (optionnel)</summary>
            <textarea rows={4} placeholder="Message NL" value={form.body_nl} onChange={(e) => setForm({ ...form, body_nl: e.target.value })} className="w-full border border-slate-300 px-3 py-2 mt-2" data-testid="ann-body-nl" />
            <textarea rows={4} placeholder="Message EN" value={form.body_en} onChange={(e) => setForm({ ...form, body_en: e.target.value })} className="w-full border border-slate-300 px-3 py-2 mt-2" data-testid="ann-body-en" />
          </details>

          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Pièces jointes (PDF / images)")}</div>
            <input type="file" multiple accept="application/pdf,image/*" onChange={(e) => setFiles(Array.from(e.target.files))} className="block text-sm" data-testid="ann-files" />
            {files.length > 0 && <div className="text-xs text-slate-500 mt-1">{files.length} fichier(s) sélectionné(s)</div>}
          </div>

          <label className="flex items-center gap-2 text-sm text-navy">
            <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} data-testid="ann-send-email" />
            Envoyer aussi par email (via Resend)
          </label>

          {err && <div className="text-red-600 text-sm" data-testid="ann-err">{err}</div>}

          <div className="flex gap-3">
            <button type="submit" disabled={sending} className="flex-1 bg-club-red text-white py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red-dark disabled:opacity-60" data-testid="ann-submit">
              {sending ? tr("Envoi…") : tr("Envoyer l'annonce")}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-5 bg-slate-200 text-navy py-3 text-xs uppercase tracking-[0.25em] hover:bg-slate-300" data-testid="ann-cancel">{tr("Annuler")}</button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="ann-list-empty">{tr("Aucune annonce envoyée.")}</div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="bg-white border border-slate-200 p-5" data-testid={`ann-item-${a.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 font-semibold bg-club-red text-white">{a.type.replace(/_/g, " ")}</span>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 font-semibold bg-navy text-white">{audienceLabel(a.audience)}</span>
                  <span className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString("fr")}</span>
                </div>
                <button onClick={() => del(a.id)} className="text-red-600 hover:text-red-800" data-testid={`ann-del-${a.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <h4 className="font-serif text-lg text-navy">{a.title_fr}</h4>
              <p className="text-sm text-slate-700 mt-1 line-clamp-3">{a.body_fr}</p>
              <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-500">
                <span>👥 {a.recipients_count} {a.recipients_count > 1 ? tr("destinataires") : tr("destinataire")}</span>
                <span>✓ {a.read_count}/{a.total_count} {tr("lu(s)")}</span>
                {a.attachments?.length > 0 && <span>📎 {a.attachments.length} PJ</span>}
                {a.email_result && <span className={a.email_result.status === "ok" ? "text-green-700" : "text-orange-600"}>📧 {a.email_result.status} ({a.email_result.sent} {tr("envoyé")})</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ====================== Gallery Admin Tab (RYCM iter 25) ====================== */
export function GalleryAdminTab() {
  const [albums, setAlbums] = useState([]);
  const [events, setEvents] = useState([]);
  const [regattas, setRegattas] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [photosOf, setPhotosOf] = useState(null);
  const [form, setForm] = useState({
    title: "", year: new Date().getFullYear(), date: "",
    event_id: "", regatta_slug: "", description: "",
  });

  const backend = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = (u) => (u && u.startsWith("/uploads/") ? `${backend}/api${u}` : u);

  const load = async () => {
    try {
      const [a, e, r] = await Promise.all([
        api.get("/gallery/albums"),
        api.get("/admin/events").catch(() => ({ data: [] })),
        api.get("/regattas").catch(() => ({ data: [] })),
      ]);
      setAlbums(a.data || []); setEvents(e.data || []); setRegattas(r.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const createAlbum = async () => {
    if (!form.title || !form.year) { toast.error(tr("Titre et année obligatoires")); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => v && fd.append(k, String(v)));
    try {
      await api.post("/admin/gallery/albums", fd);
      toast.success(tr("Album créé"));
      setCreating(false);
      setForm({ title: "", year: new Date().getFullYear(), date: "", event_id: "", regatta_slug: "", description: "" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const saveEdit = async () => {
    const fd = new FormData();
    Object.entries(editing).forEach(([k, v]) => {
      if (["title","year","date","event_id","regatta_slug","description"].includes(k) && v !== null && v !== undefined) fd.append(k, String(v));
    });
    try {
      await api.patch(`/admin/gallery/albums/${editing.id}`, fd);
      toast.success(tr("Album mis à jour"));
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const delAlbum = async (a) => {
    if (!window.confirm(`Supprimer l'album « ${a.title} » et toutes ses photos ?`)) return;
    try {
      await api.delete(`/admin/gallery/albums/${a.id}`);
      toast.success(tr("Album supprimé"));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="gallery-admin-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-navy">{tr("Galerie photos")}</h2>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">{albums.length} album(s)</div>
        </div>
        <button onClick={() => setCreating(true)}
          className="bg-club-red text-white text-xs uppercase tracking-widest px-5 py-3 hover:bg-club-red-dark"
          data-testid="gallery-create-btn">
          <Plus className="w-3 h-3 inline mr-1" /> Nouvel album
        </button>
      </div>

      {creating && (
        <AlbumForm
          form={form} setForm={setForm} events={events} regattas={regattas}
          onSubmit={createAlbum} onCancel={() => setCreating(false)}
        />
      )}
      {editing && (
        <AlbumForm
          form={editing} setForm={setEditing} events={events} regattas={regattas}
          onSubmit={saveEdit} onCancel={() => setEditing(null)} editing
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {albums.map((a) => (
          <div key={a.id} className="bg-white border border-slate-200 overflow-hidden" data-testid={`gallery-album-card-${a.id}`}>
            <div className="aspect-[4/3] bg-sand">
              {a.cover_image ? (
                <img src={fullUrl(a.cover_image)} alt={a.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Images className="w-10 h-10 text-slate-300" /></div>
              )}
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-club-yellow-dark font-semibold mb-1">{a.year}{a.date && ` · ${a.date}`}</div>
              <h3 className="font-serif text-navy text-lg">{a.title}</h3>
              <div className="text-[11px] text-slate-500 mt-1">{a.photo_count} {tr("photo(s)")}</div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={() => setPhotosOf(a)}
                  className="text-[11px] uppercase tracking-widest text-navy border border-navy px-3 py-1.5 hover:bg-navy hover:text-white"
                  data-testid={`gallery-photos-${a.id}`}>
                  <Upload className="w-3 h-3 inline mr-1" /> Photos
                </button>
                <button onClick={() => setEditing(a)}
                  className="text-[11px] uppercase tracking-widest text-club-yellow-dark border border-club-yellow-dark px-3 py-1.5 hover:bg-club-yellow"
                  data-testid={`gallery-edit-${a.id}`}>{tr("Modifier")}</button>
                <button onClick={() => delAlbum(a)}
                  className="text-[11px] uppercase tracking-widest text-red-600 hover:underline ml-auto"
                  data-testid={`gallery-del-${a.id}`}>{tr("Supprimer")}</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {photosOf && <PhotosModal album={photosOf} onClose={() => { setPhotosOf(null); load(); }} />}
    </div>
  );
}

function AlbumForm({ form, setForm, onSubmit, onCancel, events, regattas, editing = false }) {
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  return (
    <div className="bg-sand border border-slate-200 p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="gallery-album-form">
      <Field label={tr("Titre *")} value={form.title || ""} onChange={(v) => set("title", v)} testid="album-title" />
      <Field label={tr("Année *")} type="number" value={form.year || ""} onChange={(v) => set("year", Number(v))} testid="album-year" />
      <Field label={tr("Date")} type="date" value={form.date || ""} onChange={(v) => set("date", v)} testid="album-date" />
      <div>
        <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{tr("Régate liée")}</label>
        <select value={form.regatta_slug || ""} onChange={(e) => set("regatta_slug", e.target.value)}
          className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="album-regatta">
          <option value="">—</option>
          {regattas.map((r) => <option key={r.slug} value={r.slug}>{r.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{tr("Événement lié")}</label>
        <select value={form.event_id || ""} onChange={(e) => set("event_id", e.target.value)}
          className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="album-event">
          <option value="">—</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.title_fr} ({(e.date || "").slice(0, 10)})</option>)}
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{tr("Description")}</label>
        <textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={2}
          className="w-full border border-slate-300 px-3 py-2 text-sm bg-white resize-none" data-testid="album-description" />
      </div>
      <div className="md:col-span-2 flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-xs uppercase tracking-widest text-slate-600 hover:text-navy" data-testid="album-cancel">{tr("Annuler")}</button>
        <button onClick={onSubmit} className="px-4 py-2 text-xs uppercase tracking-widest bg-navy text-white hover:bg-club-red" data-testid="album-submit">
          {editing ? tr("Enregistrer") : tr("Créer")}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-navy"
        data-testid={testid} />
    </div>
  );
}

function PhotosModal({ album, onClose }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = (u) => (u && u.startsWith("/uploads/") ? `${backend}/api${u}` : u);

  const load = async () => {
    try {
      const { data } = await api.get(`/gallery/albums/${album.slug}`);
      setPhotos(data.photos || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [album.id]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/admin/gallery/albums/${album.id}/photos`, fd);
      } catch (err) {
        toast.error(`${f.name} : ${err.response?.data?.detail || "erreur"}`);
      }
    }
    setUploading(false);
    e.target.value = "";
    await load();
    toast.success(`${files.length} ${tr("photo(s) uploadée(s)")}`);
  };

  const delPhoto = async (p) => {
    if (!window.confirm(tr("Supprimer cette photo ?"))) return;
    try {
      await api.delete(`/admin/gallery/photos/${p.id}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="gallery-photos-modal">
      <div className="bg-white max-w-5xl w-full my-8">
        <div className="bg-navy text-white px-6 py-4 flex items-center justify-between sticky top-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-80">{album.year} · Album</div>
            <div className="font-serif text-xl">{album.title}</div>
          </div>
          <button onClick={onClose} className="hover:opacity-70" data-testid="gallery-photos-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">
          <label className="inline-flex items-center gap-2 bg-club-yellow text-navy px-5 py-3 text-xs uppercase tracking-widest cursor-pointer hover:bg-club-yellow-dark" data-testid="gallery-upload-label">
            <Upload className="w-4 h-4" />
            {uploading ? "Upload en cours…" : tr("Uploader des photos (multi-fichier)")}
            <input type="file" multiple accept="image/*" onChange={upload} className="hidden" data-testid="gallery-upload-input" />
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-6">
            {photos.length === 0 && <div className="col-span-full text-center text-slate-500 py-8" data-testid="gallery-photos-empty">{tr("Aucune photo dans cet album.")}</div>}
            {photos.map((p) => (
              <div key={p.id} className="relative group" data-testid={`gallery-photo-${p.id}`}>
                <img src={fullUrl(p.url)} alt={p.caption || ""} className="w-full aspect-square object-cover" />
                <button onClick={() => delPhoto(p)} className="absolute top-2 right-2 bg-red-600 text-white w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`gallery-photo-del-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ====================== Partners Admin Tab (RYCM iter 29) ====================== */
export function PartnersTab() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", logo_url: "", link_url: "", order: 0, active: true });
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = (u) => (u && u.startsWith("/uploads/") ? `${backend}/api${u}` : u);

  const load = async () => {
    try { const { data } = await api.get("/admin/partners"); setItems(data || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.logo_url) { toast.error(tr("Nom et logo obligatoires")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      await api.post("/admin/partners", fd);
      toast.success(tr("Partenaire ajouté"));
      setForm({ name: "", logo_url: "", link_url: "", order: items.length + 1, active: true });
      setCreating(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const update = async (p, patch) => {
    try {
      const fd = new FormData();
      Object.entries(patch).forEach(([k, v]) => fd.append(k, String(v)));
      await api.patch(`/admin/partners/${p.id}`, fd);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Supprimer le logo « ${p.name} » ?`)) return;
    try { await api.delete(`/admin/partners/${p.id}`); await load(); toast.success(tr("Supprimé")); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const uploadLogo = async (file, setter) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/partners/upload-logo", fd);
      setter(data.url);
      toast.success(tr("Logo uploadé"));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur upload"); }
  };

  return (
    <div data-testid="partners-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-navy">{tr("Partenaires & Soutiens")}</h2>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">{items.length} {tr("logo(s) — affichés en bas de chaque page")}</div>
        </div>
        <button onClick={() => setCreating(true)}
          className="bg-club-red text-white text-xs uppercase tracking-widest px-5 py-3 hover:bg-club-red-dark"
          data-testid="partners-create-btn">
          <Plus className="w-3 h-3 inline mr-1" />{tr("Ajouter")}</button>
      </div>

      {creating && (
        <div className="bg-sand border border-slate-200 p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="partner-form">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{tr("Nom *")}</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="partner-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Lien (URL site)</label>
            <input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })}
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="partner-link" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">URL du logo *</label>
            <div className="flex gap-2">
              <input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="https://… ou /uploads/partners/…"
                className="flex-1 border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="partner-logo-url" />
              <label className="inline-flex items-center gap-2 bg-club-yellow text-navy px-4 py-2 text-xs uppercase tracking-widest cursor-pointer hover:bg-club-yellow-dark" data-testid="partner-upload-label">
                <Upload className="w-3 h-3" /> Uploader
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0], (url) => setForm((f) => ({ ...f, logo_url: url })))}
                  data-testid="partner-upload-input" />
              </label>
            </div>
            {form.logo_url && <img src={fullUrl(form.logo_url)} alt={tr("Aperçu")} className="mt-3 h-16 w-auto object-contain" />}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Ordre d'affichage</label>
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="partner-order" />
          </div>
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-2 text-sm text-navy">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="partner-active" />
              Visible publiquement
            </label>
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button onClick={() => setCreating(false)} className="px-4 py-2 text-xs uppercase tracking-widest text-slate-600">{tr("Annuler")}</button>
            <button onClick={submit} disabled={busy}
              className="px-4 py-2 text-xs uppercase tracking-widest bg-navy text-white hover:bg-club-red"
              data-testid="partner-submit">{tr("Créer")}</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-sand">
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-600 border-b border-slate-200">
              <th className="px-3 py-3">Logo</th>
              <th className="px-3">{tr("Nom")}</th>
              <th className="px-3">URL site</th>
              <th className="px-3 text-center">{tr("Ordre")}</th>
              <th className="px-3 text-center">{tr("Visible")}</th>
              <th className="px-3">{tr("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="text-center text-slate-500 py-8" data-testid="partners-empty">{tr("Aucun partenaire — ajoutez-en avec le bouton ci-dessus.")}</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-slate-100" data-testid={`partner-row-${p.id}`}>
                <td className="px-3 py-2"><img src={fullUrl(p.logo_url)} alt={p.name} className="h-10 w-auto object-contain" onError={(e) => { e.target.style.opacity = "0.3"; }} /></td>
                <td className="px-3 font-serif text-navy">{p.name}</td>
                <td className="px-3 text-[11px] text-slate-500 truncate max-w-[200px]" title={p.link_url}>{p.link_url || "—"}</td>
                <td className="px-3 text-center">
                  <input type="number" defaultValue={p.order} onBlur={(e) => Number(e.target.value) !== p.order && update(p, { order: Number(e.target.value) })}
                    className="w-16 border border-slate-300 px-2 py-1 text-center" data-testid={`partner-order-${p.id}`} />
                </td>
                <td className="px-3 text-center">
                  <input type="checkbox" checked={p.active !== false} onChange={(e) => update(p, { active: e.target.checked })} data-testid={`partner-active-${p.id}`} />
                </td>
                <td className="px-3">
                  <button onClick={() => del(p)} className="text-[11px] uppercase tracking-widest text-red-600 hover:underline" data-testid={`partner-del-${p.id}`}>
                    <Trash2 className="w-3 h-3 inline mr-1" />{tr("Supprimer")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UsefulLinksManager />
    </div>
  );
}


/* ====================== Liens utiles / Inscriptions externes ====================== */
const EMPTY_LINK = {
  title_fr: "", title_nl: "", title_en: "",
  description_fr: "", description_nl: "", description_en: "",
  button_fr: "", button_nl: "", button_en: "",
  url: "", logo_url: "", category_id: "", visibility: "member", active: true,
};

function UsefulLinksManager() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_LINK);
  const [showCats, setShowCats] = useState(false);
  const [newCat, setNewCat] = useState({ name_fr: "", name_nl: "", name_en: "" });
  const dragIdx = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = (u) => (u && u.startsWith("/uploads/") ? `${backend}/api${u}` : u);

  const load = async () => {
    try {
      const [l, c] = await Promise.all([api.get("/admin/useful-links"), api.get("/link-categories")]);
      setItems(l.data || []); setCats(c.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing && editing !== "new") {
        await api.patch(`/admin/useful-links/${editing}`, form);
        toast.success(tr("Lien mis à jour"));
      } else {
        await api.post("/admin/useful-links", form);
        toast.success(tr("Lien créé"));
      }
      setEditing(null); setForm(EMPTY_LINK); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (l) => {
    if (!window.confirm(tr("Supprimer ce lien ?"))) return;
    try { await api.delete(`/admin/useful-links/${l.id}`); toast.success(tr("Lien supprimé")); await load(); }
    catch { toast.error("Erreur"); }
  };

  const toggleActive = async (l) => {
    try { await api.patch(`/admin/useful-links/${l.id}`, { ...EMPTY_LINK, ...l, active: !(l.active !== false) }); await load(); }
    catch { toast.error("Erreur"); }
  };

  const reorder = async (next) => {
    setItems(next);
    try { await api.post("/admin/useful-links/reorder", { ids: next.map((x) => x.id) }); toast.success(tr("Ordre mis à jour")); }
    catch { toast.error("Erreur"); await load(); }
  };

  const dragProps = (i) => ({
    draggable: true,
    onDragStart: () => { dragIdx.current = i; },
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => {
      e.preventDefault();
      const from = dragIdx.current;
      if (from === null || from === i) return;
      const next = [...items];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      dragIdx.current = null;
      reorder(next);
    },
  });

  const uploadLogo = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/admin/partners/upload-logo", fd);
      setForm((prev) => ({ ...prev, logo_url: data.url }));
      toast.success(tr("Logo uploadé"));
    } catch { toast.error(tr("Erreur lors de l'upload")); }
  };

  const addCat = async () => {
    if (!newCat.name_fr.trim()) return;
    try {
      await api.post("/admin/link-categories", { name_fr: newCat.name_fr.trim(), name_nl: newCat.name_nl.trim(), name_en: newCat.name_en.trim() });
      setNewCat({ name_fr: "", name_nl: "", name_en: "" });
      await load();
    } catch { toast.error("Erreur"); }
  };
  const delCat = async (c) => {
    if (!window.confirm(tr("Supprimer cette catégorie ? Les liens associés deviendront « Sans catégorie »."))) return;
    try { await api.delete(`/admin/link-categories/${c.id}`); await load(); } catch { toast.error("Erreur"); }
  };

  const catName = (id) => cats.find((c) => c.id === id)?.name_fr || "";
  const VIS_LBL = { member: "Espace Membre", public: "Public", both: "Les deux" };

  return (
    <div className="mt-12 border-t border-slate-200 pt-8" data-testid="useful-links-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h4 className="font-serif text-xl text-navy">{tr("Liens utiles / Inscriptions externes")}</h4>
        <div className="flex gap-2">
          <button onClick={() => setShowCats(!showCats)} className="border border-slate-300 text-navy px-4 py-2 text-xs uppercase tracking-widest hover:border-club-red" data-testid="link-cats-toggle">
            {tr("Gérer les catégories")}
          </button>
          <button onClick={() => { setEditing("new"); setForm(EMPTY_LINK); }} className="bg-navy text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-club-red inline-flex items-center gap-2" data-testid="link-create-btn">
            <Plus className="w-3.5 h-3.5" /> {tr("Ajouter un lien")}
          </button>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-4">{tr("Glissez-déposez pour réordonner. Géré depuis l'admin, affiché dans Mon Espace et/ou sur le site public.")}</p>

      {showCats && (
        <div className="border border-slate-200 bg-slate-50 p-4 mb-5" data-testid="link-cats-panel">
          <div className="flex flex-wrap gap-2 mb-3">
            {cats.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 text-xs" data-testid={`link-cat-${c.id}`}>
                {c.name_fr}
                <button onClick={() => delCat(c)} className="text-red-500 hover:text-red-700" data-testid={`link-cat-del-${c.id}`}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <input value={newCat.name_fr} onChange={(e) => setNewCat({ ...newCat, name_fr: e.target.value })} placeholder={tr("Nouvelle catégorie (FR)")} className="border border-slate-300 px-3 py-2 text-sm flex-1 min-w-[140px] max-w-xs" data-testid="link-cat-input" />
            <input value={newCat.name_nl} onChange={(e) => setNewCat({ ...newCat, name_nl: e.target.value })} placeholder="NL" className="border border-slate-300 px-3 py-2 text-sm w-28" data-testid="link-cat-input-nl" />
            <input value={newCat.name_en} onChange={(e) => setNewCat({ ...newCat, name_en: e.target.value })} placeholder="EN" className="border border-slate-300 px-3 py-2 text-sm w-28" data-testid="link-cat-input-en" />
            <button onClick={addCat} className="bg-navy text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-club-red" data-testid="link-cat-add">{tr("Ajouter")}</button>
          </div>
        </div>
      )}

      {editing && (
        <div className="border border-slate-200 bg-slate-50 p-5 mb-6 space-y-4" data-testid="link-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {["fr", "nl", "en"].map((lg) => (
              <input key={`t-${lg}`} value={form[`title_${lg}`]} onChange={(e) => setForm({ ...form, [`title_${lg}`]: e.target.value })}
                placeholder={`${tr("Titre")} ${lg.toUpperCase()}${lg === "fr" ? " *" : ""}`} className="border border-slate-300 px-3 py-2 text-sm" data-testid={`link-title-${lg}`} />
            ))}
            {["fr", "nl", "en"].map((lg) => (
              <input key={`d-${lg}`} value={form[`description_${lg}`]} onChange={(e) => setForm({ ...form, [`description_${lg}`]: e.target.value })}
                placeholder={`Description ${lg.toUpperCase()}`} className="border border-slate-300 px-3 py-2 text-sm" data-testid={`link-desc-${lg}`} />
            ))}
            {["fr", "nl", "en"].map((lg) => (
              <input key={`b-${lg}`} value={form[`button_${lg}`]} onChange={(e) => setForm({ ...form, [`button_${lg}`]: e.target.value })}
                placeholder={`${tr("Texte du bouton")} ${lg.toUpperCase()}`} className="border border-slate-300 px-3 py-2 text-sm" data-testid={`link-button-${lg}`} />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="URL * (https://…)" className="border border-slate-300 px-3 py-2 text-sm" data-testid="link-url" />
            <div className="flex items-center gap-3">
              {form.logo_url && <img src={fullUrl(form.logo_url)} alt="logo" className="w-9 h-9 object-contain border border-slate-200 bg-white" />}
              <label className="border border-slate-300 bg-white px-3 py-2 text-xs uppercase tracking-widest cursor-pointer hover:border-club-red">
                {tr("Logo / icône (optionnel)")}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} data-testid="link-logo-input" />
              </label>
            </div>
            <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="link-category">
              <option value="">{tr("Sans catégorie")}</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name_fr}</option>)}
            </select>
            <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className="border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="link-visibility">
              <option value="member">{tr("Visible dans")} : {tr("Espace Membre")}</option>
              <option value="public">{tr("Visible dans")} : {tr("Public")}</option>
              <option value="both">{tr("Visible dans")} : {tr("Les deux")}</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="bg-club-red text-white px-6 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="link-save">{tr("Enregistrer")}</button>
            <button onClick={() => { setEditing(null); setForm(EMPTY_LINK); }} className="border border-slate-300 px-6 py-2.5 text-xs uppercase tracking-[0.2em] hover:border-navy" data-testid="link-cancel">{tr("Annuler")}</button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-slate-400 text-sm" data-testid="links-empty">{tr("Aucun lien — ajoutez-en avec le bouton ci-dessus.")}</div>
      ) : (
        <div className="space-y-2" data-testid="links-list">
          {items.map((l, i) => (
            <div key={l.id} {...dragProps(i)} className={`flex flex-wrap items-center gap-3 border border-slate-200 bg-white px-4 py-3 cursor-grab ${l.active === false ? "opacity-50" : ""}`} data-testid={`link-row-${l.id}`}>
              <GripVertical className="w-4 h-4 text-slate-300 shrink-0" />
              {l.logo_url ? <img src={fullUrl(l.logo_url)} alt="" className="w-8 h-8 object-contain shrink-0" /> : <ExternalLink className="w-5 h-5 text-slate-300 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="font-medium text-navy text-sm truncate">{l.title_fr}</div>
                <div className="text-xs text-slate-400 truncate">{l.url}</div>
              </div>
              {l.category_id && catName(l.category_id) && <span className="text-[10px] uppercase tracking-widest text-slate-400">{catName(l.category_id)}</span>}
              <span className="text-[10px] uppercase tracking-widest text-slate-500 border border-slate-200 px-2 py-1">{tr(VIS_LBL[l.visibility] || "Espace Membre")}</span>
              <button onClick={() => toggleActive(l)} title={l.active === false ? tr("Activer") : tr("Désactiver")} data-testid={`link-toggle-${l.id}`}>
                {l.active === false ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-green-600" />}
              </button>
              <button onClick={() => { setEditing(l.id); setForm({ ...EMPTY_LINK, ...Object.fromEntries(Object.keys(EMPTY_LINK).map((k) => [k, l[k] ?? EMPTY_LINK[k]])) }); }}
                className="text-[11px] uppercase tracking-widest text-navy hover:underline" data-testid={`link-edit-${l.id}`}>{tr("Éditer")}</button>
              <button onClick={() => del(l)} className="text-[11px] uppercase tracking-widest text-red-600 hover:underline" data-testid={`link-del-${l.id}`}>
                <Trash2 className="w-3 h-3 inline mr-1" />{tr("Supprimer")}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
export function BoardTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = (u) => (u && u.startsWith("/uploads/") ? `${backend}/api${u}` : u);
  const empty = { name: "", role_fr: "", role_nl: "", role_en: "", image: "", bio: "", email: "", order: 0 };
  const [form, setForm] = useState(empty);

  const load = async () => {
    try { const { data } = await api.get("/admin/board"); setItems(data || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.role_fr) { toast.error(tr("Nom et fonction (FR) obligatoires")); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, String(v ?? "")));
    try {
      if (editing) await api.patch(`/admin/board/${editing.id}`, fd);
      else         await api.post("/admin/board", fd);
      toast.success(editing ? tr("Mis à jour") : tr("Ajouté"));
      setEditing(null); setCreating(false); setForm(empty);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (m) => {
    if (!window.confirm(`Supprimer ${m.name} ?`)) return;
    try { await api.delete(`/admin/board/${m.id}`); await load(); toast.success(tr("Supprimé")); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const uploadPhoto = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/board/upload-photo", fd);
      setForm((f) => ({ ...f, image: data.url }));
      toast.success(tr("Photo uploadée"));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur upload"); }
  };

  const startEdit = (m) => { setForm({ ...empty, ...m }); setEditing(m); setCreating(false); };
  const startCreate = () => { setForm({ ...empty, order: items.length + 1 }); setCreating(true); setEditing(null); };

  return (
    <div data-testid="board-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-navy">{tr("Conseil d'administration")}</h2>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">{items.length} {tr("membre(s) — affichés sur la page /club")}</div>
        </div>
        <button onClick={startCreate}
          className="bg-club-red text-white text-xs uppercase tracking-widest px-5 py-3 hover:bg-club-red-dark"
          data-testid="board-create-btn">
          <Plus className="w-3 h-3 inline mr-1" />{tr("Ajouter un membre")}</button>
      </div>

      {(creating || editing) && (
        <div className="bg-sand border border-slate-200 p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="board-form">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Photo portrait</label>
            <div className="flex items-start gap-4">
              {form.image && <img src={fullUrl(form.image)} alt={tr("Aperçu")} className="w-24 h-32 object-cover bg-slate-100" />}
              <div className="flex-1">
                <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
                  placeholder={tr("URL ou /uploads/board/…")}
                  className="w-full border border-slate-300 px-3 py-2 text-sm bg-white mb-2" data-testid="board-image-url" />
                <label className="inline-flex items-center gap-2 bg-club-yellow text-navy px-4 py-2 text-xs uppercase tracking-widest cursor-pointer hover:bg-club-yellow-dark" data-testid="board-upload-label">
                  <Upload className="w-3 h-3" /> Uploader une photo
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])}
                    data-testid="board-upload-input" />
                </label>
                <p className="text-[10px] text-slate-500 mt-2">{tr("Format recommandé : portrait JPG/PNG, ratio 4:5 (ex. 800×1000 px), 100-300 KB.")}</p>
              </div>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{tr("Nom *")}</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="board-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">E-mail</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="board-email" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Fonction FR *</label>
            <input value={form.role_fr} onChange={(e) => setForm({ ...form, role_fr: e.target.value })}
              placeholder={tr("Président, Trésorier, …")}
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="board-role-fr" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Fonction NL</label>
            <input value={form.role_nl} onChange={(e) => setForm({ ...form, role_nl: e.target.value })}
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="board-role-nl" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Fonction EN</label>
            <input value={form.role_en} onChange={(e) => setForm({ ...form, role_en: e.target.value })}
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="board-role-en" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Ordre d'affichage</label>
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="board-order" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Biographie (optionnel)</label>
            <textarea rows={2} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white resize-none" data-testid="board-bio" />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button onClick={() => { setEditing(null); setCreating(false); setForm(empty); }}
              className="px-4 py-2 text-xs uppercase tracking-widest text-slate-600" data-testid="board-cancel">{tr("Annuler")}</button>
            <button onClick={save}
              className="px-4 py-2 text-xs uppercase tracking-widest bg-navy text-white hover:bg-club-red"
              data-testid="board-submit">{editing ? tr("Enregistrer") : tr("Créer")}</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <div className="col-span-full text-center text-slate-500 py-12 bg-sand border border-dashed border-slate-300" data-testid="board-empty">{tr("Aucun membre — ajoutez-en avec le bouton ci-dessus.")}</div>}
        {items.map((m) => (
          <div key={m.id} className="bg-white border border-slate-200 overflow-hidden" data-testid={`board-card-${m.id}`}>
            <div className="aspect-[4/5] bg-slate-100">
              {m.image
                ? <img src={fullUrl(m.image)} alt={m.name} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs uppercase">{tr("Pas de photo")}</div>}
            </div>
            <div className="p-4">
              <div className="font-serif text-lg text-navy">{m.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-club-yellow-dark mt-1">{m.role_fr}</div>
              {m.email && <div className="text-[11px] text-slate-500 mt-1 truncate">{m.email}</div>}
              <div className="flex gap-2 mt-3">
                <button onClick={() => startEdit(m)}
                  className="text-[11px] uppercase tracking-widest text-navy border border-navy px-3 py-1.5 hover:bg-navy hover:text-white"
                  data-testid={`board-edit-${m.id}`}>{tr("Modifier")}</button>
                <button onClick={() => del(m)}
                  className="text-[11px] uppercase tracking-widest text-red-600 hover:underline ml-auto"
                  data-testid={`board-del-${m.id}`}>{tr("Supprimer")}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
